import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Combobox,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Option,
  Spinner,
  TagGroup,
  Text,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { ConfirmationDialog } from '../Common/Dialogs';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { customDisclaimerService } from '../../services/customDisclaimerService';
import type {
  DisclaimerPolicyOption,
  DisclaimerPolicyPickerProps,
} from '../../types/customDisclaimers';
import { filterDisclaimerPolicyOptions } from '../../utils/disclaimerPolicyPickerUtils';
import { useStyles } from './DisclaimerPolicyPicker.style';

export const DisclaimerPolicyPicker: React.FC<DisclaimerPolicyPickerProps> = ({
  selectedPolicyIds,
  onSelectionChange,
  currentDisclaimerId,
  disabled = false,
}) => {
  const styles = useStyles();
  const { dialogProps, confirm } = useConfirmDialog();
  const [policyOptions, setPolicyOptions] = useState<DisclaimerPolicyOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPolicyOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const options = await customDisclaimerService.getPolicyOptions(currentDisclaimerId);
      setPolicyOptions(options);
    } catch {
      setError('Unable to load policies. Try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentDisclaimerId]);

  useEffect(() => {
    loadPolicyOptions();
  }, [loadPolicyOptions]);

  const filteredOptions = filterDisclaimerPolicyOptions(
    policyOptions,
    selectedPolicyIds,
    searchQuery,
  );
  const selectedPolicies = selectedPolicyIds
    .map((policyId) => policyOptions.find((option) => option.id === policyId))
    .filter((option): option is DisclaimerPolicyOption => Boolean(option));

  const handlePolicySelect = (
    _event: unknown,
    data: { optionValue: string | undefined },
  ) => {
    const selectedPolicy = policyOptions.find((option) => option.id === data.optionValue);
    if (!selectedPolicy || selectedPolicy.disabled || selectedPolicyIds.includes(selectedPolicy.id)) {
      return;
    }

    onSelectionChange([...selectedPolicyIds, selectedPolicy.id]);
    setSearchQuery('');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const requestPolicyRemoval = (policy: DisclaimerPolicyOption) => {
    confirm(
      'Remove policy?',
      `Remove ${policy.name} from this disclaimer?`,
      () => onSelectionChange(selectedPolicyIds.filter((policyId) => policyId !== policy.id)),
    );
  };

  return (
    <div className={styles.container} data-testid="disclaimer-policy-picker">
      <label className={styles.label} htmlFor="disclaimer-policy-search">
        Assign to policies
      </label>
      <Text id="disclaimer-policy-helper" className={styles.helperText}>
        Select external policies to assign this disclaimer to, or leave it unassigned. Policies assigned to another disclaimer cannot be selected.
      </Text>

      {loading && (
        <div className={styles.loading} data-testid="disclaimer-policy-picker-loading">
          <Spinner size="tiny" />
          <Text size={200}>Loading policies...</Text>
        </div>
      )}

      {error && (
        <div className={styles.error} data-testid="disclaimer-policy-picker-error">
          <Text size={200}>{error}</Text>
          <Button appearance="secondary" size="small" onClick={loadPolicyOptions}>
            Try again
          </Button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className={styles.comboboxContainer}>
            <SearchRegular className={styles.searchIcon} />
            <Combobox
              id="disclaimer-policy-search"
              className={styles.policyCombobox}
              input={{ className: styles.policyComboboxInput }}
              value={searchQuery}
              placeholder="Search policies..."
              size="small"
              disabled={disabled}
              expandIcon={null}
              inlinePopup
              positioning={{ position: 'below', align: 'start', pinned: true }}
              listbox={{
                className: styles.policyOptionsList,
              }}
              aria-describedby="disclaimer-policy-helper"
              onChange={handleSearchChange}
              onOptionSelect={handlePolicySelect}
              data-testid="disclaimer-policy-picker-search-input"
            >
              {filteredOptions.map((option) => (
                <Option
                  key={option.id}
                  value={option.id}
                  text={option.name}
                  disabled={option.disabled}
                  className={option.disabled ? undefined : styles.policyOptionAvailable}
                  data-testid={`disclaimer-policy-option-${option.id}`}
                >
                  {option.name}
                </Option>
              ))}
              {filteredOptions.length === 0 && (
                <Option disabled value="no-policies-found" text="No policies found" data-testid="disclaimer-policy-no-results">
                  No policies found
                </Option>
              )}
            </Combobox>
          </div>

          <div className={styles.selectedPolicies} data-testid="disclaimer-selected-policies">
            {selectedPolicies.length === 0 ? (
              <Text className={styles.emptySelection}>No policies selected</Text>
            ) : (
              <TagGroup className={styles.selectedPolicyTags} aria-label="Selected policies">
                {selectedPolicies.map((policy) => (
                  <InteractionTag
                    key={policy.id}
                    className={styles.selectedPolicyTag}
                    appearance="brand"
                    size="medium"
                  >
                    <InteractionTagPrimary className={styles.selectedPolicyTagPrimary}>
                      {policy.name}
                    </InteractionTagPrimary>
                    {!disabled && (
                      <InteractionTagSecondary
                        aria-label={`Remove ${policy.name}`}
                        onClick={() => requestPolicyRemoval(policy)}
                        data-testid={`disclaimer-remove-policy-${policy.id}`}
                      >
                        <DismissRegular />
                      </InteractionTagSecondary>
                    )}
                  </InteractionTag>
                ))}
              </TagGroup>
            )}
          </div>
        </>
      )}

      <ConfirmationDialog
        {...dialogProps}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  );
};